import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import type { OverviewAnalysisResult } from "../../modules/overviewAnalysis/types";
import { useEffect, useState } from "react";
import { analyzeOverview } from "../../modules/overviewAnalysis/index.mjs";
import { GD } from "src/GD";
import { getPreviousOverviewPeriod, loadOverview, selectAnalysisWidgets } from "./overviewData";

export type OverviewPeriodKind = "today" | "yesterday" | "last_7_days" | "last_30_days" | "this_month" | "last_month" | "custom";

export interface OverviewAnalysisFilters {
	target_id: number;
	target_type: "client" | "merchant" | "project";
	from: string;
	to: string;
	currency: string;
	channel_id?: number;
	/** Payment instruments (card, ap, gp, ob); omitted means all. */
	instruments?: string[];
	/** Project dashboards only: limit to these clients; omitted means all. */
	client_ids?: number[];
	period_kind: OverviewPeriodKind;
	size: number;
}

export type { OverviewEntity, OverviewBankEntity, OverviewIssueDetail, OverviewAnalysisResult } from "../../modules/overviewAnalysis/types";

export interface OverviewAnalysis extends OverviewAnalysisResult {
 generatedAt: string;
 expiresAt: string;
 cached: boolean;
 source: "rules";
}

type AnalysisState = { key: string; data?: OverviewAnalysis; error?: string; busy: boolean };
// Local results stay in this tab's memory, partitioned by user/project/filters.
const pending = new Map<string, Promise<OverviewAnalysis>>();
const cache = new Map<string, OverviewAnalysis>();
const REQUEST_DELAY_MS = 250;
const REQUEST_TIMEOUT_MS = 60_000;
const MAX_CACHE_ENTRIES = 24;
let sessionEpoch = 0;
let subscribed = false;

// Exported for integration tests. Only loadOverview performs network requests (CRM).
export const loadAnalysis = async (key: string, filters: OverviewAnalysisFilters, userId: number, projectId: number): Promise<OverviewAnalysis> => {
 if (!subscribed) {
  subscribed = true;
  GD.S_AUTH_LOG_OUT.subscribe(() => { sessionEpoch += 1; cache.clear(); pending.clear(); });
 }
 const epoch = sessionEpoch;
 const assertSession = async () => {
  const [user, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
  if (epoch !== sessionEpoch || user?.id !== userId || project?.id !== projectId) throw new Error("Session changed");
 };
 await assertSession();
 const cached = cache.get(key);
 if (cached && Date.parse(cached.expiresAt) > Date.now()) return { ...cached, cached: true };
 cache.delete(key);
 const existing = pending.get(key);
 if (existing) return existing;
 const request = (async (): Promise<OverviewAnalysis> => {
  const { period_kind, ...overviewFilters } = filters;
  const previousPeriod = getPreviousOverviewPeriod(filters);
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const deadline = new Promise<never>((_, reject) => controller.signal.addEventListener("abort", () => reject(new Error("Overview timed out")), { once: true }));
  try {
   const current = await Promise.race([loadOverview(overviewFilters, userId, projectId), deadline]);
   const previous = await Promise.race([loadOverview({ ...overviewFilters, ...previousPeriod }, userId, projectId), deadline]).catch(() => null);
   await assertSession();
   const rules = analyzeOverview({
    version: 1, userId, projectId,
    scope: { targetType: filters.target_type, targetId: filters.target_id, currency: filters.currency, channelId: filters.channel_id ?? null },
    period: { from: filters.from, to: filters.to, partial: !["yesterday", "last_month"].includes(period_kind) },
    current: selectAnalysisWidgets(current),
    previous: previous ? { period: { ...previousPeriod, partial: false }, data: selectAnalysisWidgets(previous) } : null,
   });
   const result: OverviewAnalysis = {
    ...rules, source: "rules", cached: false, generatedAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + (previous ? 5 * 60_000 : 30_000)).toISOString(),
   };
   cache.set(key, result);
   while (cache.size > MAX_CACHE_ENTRIES) cache.delete(cache.keys().next().value!);
   return result;
  } finally { clearTimeout(timer); }
 })();
 pending.set(key, request);
 void request.finally(() => { if (pending.get(key) === request) pending.delete(key); }).catch(() => undefined);
 return request;
};

export const useOverviewAnalysis = (filters: OverviewAnalysisFilters, userId: number, projectId: number) => {
	useLocale();
	const { target_id, target_type, from, to, currency, channel_id, instruments, client_ids, period_kind, size } = filters;
	const key = JSON.stringify([userId, projectId, target_type, target_id, from, to, currency, channel_id, instruments, client_ids, period_kind]);
	const [state, setState] = useState<AnalysisState>({ key, busy: true });
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		let cancelled = false;
		setState({ key, busy: true });
		// Switching filters quickly should not start a series of redundant CRM requests.
		const timer = setTimeout(() => {
			void loadAnalysis(key, {
				target_id, target_type, from, to, currency, channel_id, period_kind, size,
				...(instruments?.length ? { instruments } : {}),
				...(client_ids?.length ? { client_ids } : {}),
			}, userId, projectId).then(
				data => { if (!cancelled) setState({ key, data, busy: false }); },
				() => {
					if (!cancelled) setState({ key, busy: false, error: msg("message_c535251b") });
				},
			);
		}, REQUEST_DELAY_MS);
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	// instruments/client_ids are arrays: `key` carries their contents, so the effect does not depend on their identity.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [key, size, attempt]);

	// Effects run after paint: never expose a previous target's result while a new
	// filter selection is waiting for its effect to run.
	const current = state.key === key ? state : { key, busy: true };
	return { ...current, retry: () => setAttempt(value => value + 1) };
};
